import { Button, Form, Input, Modal } from "antd";
import React, { useEffect, useState } from "react";
import { useAppDispatch } from "../../../../store";
import { renderFormLabel } from "../../../../Components/Components";
import { Rule } from "antd/es/form";
import { createSubAdminAPI, updateSubAdminAPI } from "../subAdminApis";
import { decryptByText } from "../../../../helpers/bycrypt";

interface Props {
  open: boolean;
  onClose: () => void;
  refreshData?: () => void;
  subAdminData?: any | null;
}

const CreateSubAdminModal: React.FC<Props> = ({
  open,
  onClose,
  refreshData,
  subAdminData,
}) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (subAdminData) {
        form.setFieldsValue({
          firstName: decryptByText(subAdminData.first_name),
          lastName: decryptByText(subAdminData.last_name),
          email: decryptByText(subAdminData.email),
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, subAdminData, form]);

  const firstNameRules: Rule[] = [
    { required: true, message: "Please enter first name" },
    { whitespace: true, message: "Name cannot be empty" },
  ];

  const lastNameRules: Rule[] = [
    { required: true, message: "Please enter last name" },
    { whitespace: true, message: "Name cannot be empty" },
  ];

  const emailRules: Rule[] = [
    { required: true, message: "Please enter email" },
    {
      type: "email",
      message: "Please enter a valid email",
    },
  ];

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload: any = {
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
      };

      const commonCallbacks = {
        onSuccess: () => {
          setLoading(false);
          onClose();
          refreshData?.();
        },
        onError: () => {
          setLoading(false);
        },
      };

      if (subAdminData?._id) {
        // --- UPDATE MODE ---
        payload.id = subAdminData._id;
        dispatch(
          updateSubAdminAPI({
            payload: payload,
            ...commonCallbacks,
          })
        );
      } else {
        dispatch(
          createSubAdminAPI({
            payload: payload,
            ...commonCallbacks,
          })
        );
      }
    } catch (errorInfo) {
      console.log("Validation Failed:", errorInfo);
      setLoading(false);
    }
  };

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      footer={null}
      width={"33.7rem"}
      title={
         <div className="text-center fs-4 fw-bolder lh-m">
          {subAdminData ? "Update Sub Admin" : "Create Sub Admin"}
        </div>
      }
      className="commonModalClass"
    >
      <Form layout="vertical" className="page-form" form={form}>
        <div className="d-flex gap-sm-4 flex-sm-row flex-column flex-column-reverse">
          <div style={{ flex: 1 }}>
            {/* ✅ Side-by-Side Names Container */}
            <div className="d-flex gap-3">
              <Form.Item
                label={renderFormLabel("First Name", true)}
                name="firstName"
                required={false}
                rules={firstNameRules}
                className="w-50" // Occupy 50% width
              >
                <Input
                  placeholder="Enter First Name"
                  className="form-control form-control-lg"
                />
              </Form.Item>

              <Form.Item
                label={renderFormLabel("Last Name", true)}
                name="lastName"
                required={false}
                rules={lastNameRules}
                className="w-50" // Occupy 50% width
              >
                <Input
                  placeholder="Enter Last Name"
                  className="form-control form-control-lg"
                />
              </Form.Item>
            </div>

            <Form.Item
              label={renderFormLabel("Email", true)}
              name="email"
              required={false}
              rules={emailRules}
              normalize={(value) => value?.trim()}
            >
              <Input
                placeholder="Enter Email"
                className="form-control form-control-lg"
              />
            </Form.Item>
          </div>
        </div>

        <div className="d-flex justify-content-center gap-3">
          <Button
            onClick={onClose}
            className="btn btn-lg btn-outline-secondary h-auto px-5"
            disabled={loading}
          >
            CLOSE
          </Button>

          <Button
            type="primary"
            onClick={onSave}
            className="btn btn-lg btn-primary h-auto px-5"
            loading={loading}
          >
            {subAdminData ? "UPDATE" : "SAVE"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default CreateSubAdminModal;
